import React, { useEffect, useMemo, useState, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useFindCompanyUserContext } from "libs/context/FindCompanyUserContext";
import { submitFindCompanyUserFilterData } from "model/FindCompanyUserModel";
import Loaders from "components/Common/Loaders";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const FindCompanyUser = () => {
  const { fetchFindCompanyUserContextData, findCompanyUserContextData } = useFindCompanyUserContext() || {};
  const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("FindCompanyUser");
  const initialFilterFetchedRef = useRef(false);
  const [modal1, setModal1] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [findCompanyUserData, setFindCompanyUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const toggleViewModal = () => setModal1(!modal1);

  const filteredData = useMemo(() => findCompanyUserData, [findCompanyUserData]);

  useEffect(() => {
    fetchFindCompanyUserContextData?.();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getFindCompanyUserList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const defaultColumns = [
      { Header: "ID", accessor: "id" },
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Reg Date", accessor: "regDate" },
      { Header: "Online", accessor: "online" },
      { Header: "Shift", accessor: "shift" },
      { Header: "Status", accessor: "status" },
      { Header: "Company", accessor: "company" },
    ];

    const valueReModified = findCompanyUserContextData?.table?.columns?.length
      ? findCompanyUserContextData?.table?.columns?.map((col) => ({
          Header: col?.title,
          accessor: pascalToCamel(col?.field),
          type: col?.type,
        }))
      : defaultColumns;

    setTableMetaData(valueReModified);
  }, [findCompanyUserContextData]);

  const mapRowData = (item = {}) => ({
    ...item,
    id: item?.id || item?._id || item?.userId || "",
    name: item?.name || item?.userName || "",
    email: item?.email || "",
    regDate: item?.regDate || item?.reg_date || item?.createdOn || "",
    online: item?.online || item?.isOnline || "",
    shift: item?.shift || item?.workShift || "",
    status: item?.status || "deactive",
    company: item?.company || item?.companyName || "",
  });

  const getFindCompanyUserList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitFindCompanyUserFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || [];
    const valueReModified = listData?.map((item) => mapRowData(item));

    if (response) {
      setFindCompanyUserData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(payload);
    } else {
      setFindCompanyUserData([]);
    }
    setLoading(false);
  };

  const handleFilterSubmit = async (data) => {
    await getFindCompanyUserList({
      ...data,
      search: requestPayload?.search,
      pageNumber: 1,
    });
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    const nextPayload = {
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    };
    if (!trimmedValue) {
      delete nextPayload.search;
    }
    await getFindCompanyUserList(nextPayload);
  };

  const handlePageChange = async (page) => {
    await getFindCompanyUserList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getFindCompanyUserList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-3">Find Company User</h5>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={false}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"FindCompanyUser"}
                />
              ) : (
                // <Card className="border mb-3">
                //   <CardBody>
                //     <Spinners setLoading={setLoading} />
                //   </CardBody>
                // </Card>
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Find Company User"
            handleSubmit={handleFilterSubmit}
            filterData={findCompanyUserContextData?.filters}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

FindCompanyUser.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(FindCompanyUser);
